import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, CheckCircle2, FileText, Activity, CalendarDays } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/constants";
import { useT } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PLAN_STATUS = {
  draft: "bg-slate-200 text-slate-600",
  approved: "bg-emerald-100 text-emerald-700",
  superseded: "bg-slate-100 text-slate-400",
};

export default function PatientClinical({ patientId }) {
  const { can } = useAuth();
  const t = useT();
  const [plans, setPlans] = useState([]);
  const [procs, setProcs] = useState([]);
  const [appts, setAppts] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/clinical/patients/${patientId}/treatment-plans`),
      api.get(`/clinical/patients/${patientId}/procedures`),
      api.get(`/appointments?patient_id=${patientId}`),
    ]).then(([p, pr, a]) => { setPlans(p.data); setProcs(pr.data); setAppts(a.data); })
      .finally(() => setLoading(false));
  }, [patientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/meta/procedure-catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/meta/dentists").then((r) => setDentists(r.data)).catch(() => {});
  }, []);

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>;

  return (
    <Tabs defaultValue="plans" className="mt-8">
      <TabsList>
        <TabsTrigger value="plans" data-testid="tab-plans"><FileText className="h-4 w-4 mr-2" />{t("clinical.treatmentPlans")}</TabsTrigger>
        <TabsTrigger value="procs" data-testid="tab-procedures"><Activity className="h-4 w-4 mr-2" />{t("clinical.procedures")}</TabsTrigger>
        <TabsTrigger value="appts" data-testid="tab-appointments"><CalendarDays className="h-4 w-4 mr-2" />{t("clinical.appointments")}</TabsTrigger>
      </TabsList>

      <TabsContent value="plans" className="mt-4">
        <PlansTab patientId={patientId} plans={plans} catalog={catalog} can={can} reload={load} />
      </TabsContent>
      <TabsContent value="procs" className="mt-4">
        <ProcsTab patientId={patientId} procs={procs} catalog={catalog} dentists={dentists} can={can} reload={load} />
      </TabsContent>
      <TabsContent value="appts" className="mt-4">
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {appts.length === 0 && <div className="p-10 text-center text-slate-400">{t("clinical.noAppointments")}</div>}
          {appts.map((a) => (
            <div key={a.id} className="p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-800">{a.start?.slice(0, 16).replace("T", " ")} · {a.type}</p>
                <p className="text-sm text-slate-500">{a.dentist_name}{a.chair_name ? ` · ${a.chair_name}` : ""}</p>
              </div>
              <Badge variant="secondary" className="font-normal">{t(`appointments.status_${a.status}`, a.status.replace("_", " "))}</Badge>
            </div>
          ))}
        </div>
      </TabsContent>
    </Tabs>
  );
}

function PlansTab({ patientId, plans, catalog, can, reload }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState("preliminary_quotation");
  const [items, setItems] = useState([{ procedure_code: "", tooth: "", quantity: 1, unit_price: 0 }]);
  const canQuote = can("patient_records", "create") || can("medical_records", "create");
  const canApprove = can("medical_records", "approve");

  const addItem = () => setItems([...items, { procedure_code: "", tooth: "", quantity: 1, unit_price: 0 }]);
  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const onPick = (i, code) => {
    const c = catalog.find((x) => x.code === code);
    setItems(items.map((it, idx) => idx === i ? { ...it, procedure_code: code, unit_price: c?.default_price || 0, description: c?.name } : it));
  };

  const submit = async (e) => {
    e.preventDefault();
    const clean = items.filter((it) => it.procedure_code);
    if (clean.length === 0) return toast.error(t("clinical.addProcFirst"));
    setSaving(true);
    try {
      await api.post(`/clinical/patients/${patientId}/treatment-plans`, {
        type, currency: "EUR",
        items: clean.map((it) => ({ ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price) })),
      });
      toast.success(t("clinical.planSaved"));
      setOpen(false);
      setItems([{ procedure_code: "", tooth: "", quantity: 1, unit_price: 0 }]);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const approve = async (id) => {
    try { await api.post(`/clinical/treatment-plans/${id}/approve`); toast.success(t("clinical.planApproved")); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const convert = async (id) => {
    try { await api.post(`/clinical/treatment-plans/${id}/convert-to-plan`); toast.success(t("clinical.converted")); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      {(canQuote || canApprove) && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-plan-button" className="mb-4 bg-navy hover:bg-navy-soft text-white gap-2"><Plus className="h-4 w-4" />{t("clinical.newPlan")}</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading">{t("clinical.newPlanTitle")}</DialogTitle>
              <DialogDescription>{t("clinical.newPlanDesc")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="plan-form">
              <div className="space-y-1.5">
                <Label>{t("clinical.procedure") && t("common.type")}</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="plan-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preliminary_quotation" disabled={!canQuote}>{t("clinical.preliminaryQuotation")}</SelectItem>
                    <SelectItem value="approved_plan" disabled={!canApprove}>{t("clinical.approvedPlan")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Label className="text-xs">{t("clinical.procedure")}</Label>
                      <Select value={it.procedure_code} onValueChange={(v) => onPick(i, v)}>
                        <SelectTrigger data-testid={`plan-item-proc-${i}`}><SelectValue placeholder={t("clinical.select")} /></SelectTrigger>
                        <SelectContent>
                          {catalog.map((c) => (<SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2"><Label className="text-xs">{t("clinical.tooth")}</Label><Input value={it.tooth} onChange={(e) => setItem(i, "tooth", e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">{t("clinical.qty")}</Label><Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} /></div>
                    <div className="col-span-3"><Label className="text-xs">{t("clinical.unit")}</Label><Input type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} /></div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" />{t("clinical.addLine")}</Button>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="plan-submit" disabled={saving} className="bg-navy hover:bg-navy-soft text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("clinical.savePlan")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <div className="space-y-3">
        {plans.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">{t("clinical.noPlans")}</div>}
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-${p.id}`} className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-navy">{p.type === "approved_plan" ? t("clinical.approvedPlanLabel") : t("clinical.preliminaryQuotation")}</span>
                <span className="text-xs text-slate-400">v{p.version}</span>
                <Badge className={`${PLAN_STATUS[p.status]} hover:${PLAN_STATUS[p.status]} font-normal`}>{p.status}</Badge>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-navy tabular-nums">{eur(p.total)}</span>
                {p.status === "draft" && can("medical_records", "approve") && (
                  <Button size="sm" variant="outline" onClick={() => approve(p.id)} data-testid={`approve-plan-${p.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{t("clinical.approve")}
                  </Button>
                )}
                {p.type === "preliminary_quotation" && can("medical_records", "approve") && (
                  <Button size="sm" variant="outline" onClick={() => convert(p.id)} data-testid={`convert-plan-${p.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{t("clinical.convertToPlan")}
                  </Button>
                )}
              </div>
            </div>
            <div className="text-sm text-slate-600 space-y-1">
              {p.items.map((it, i) => (
                <div key={i} className="flex justify-between border-b border-slate-50 py-1">
                  <span>{it.description || it.procedure_code}{it.tooth ? ` · tooth ${it.tooth}` : ""} × {it.quantity}</span>
                  <span className="tabular-nums">{eur(it.unit_price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">{t("clinical.by")} {p.created_by_name}{p.approved_by_name ? ` · ${t("clinical.approvedBy")} ${p.approved_by_name}` : ""}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProcsTab({ patientId, procs, catalog, dentists, can, reload }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ procedure_code: "", tooth: "", units: 1, shade: "", technical_instructions: "", dentist_id: "", status: "planned", notes: "" });
  const canEdit = can("medical_records", "create");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.procedure_code) return toast.error(t("clinical.selectProc"));
    setSaving(true);
    try {
      const c = catalog.find((x) => x.code === form.procedure_code);
      await api.post(`/clinical/patients/${patientId}/procedures`, { ...form, units: Number(form.units) || 1, description: c?.name });
      toast.success(t("clinical.procRecorded"));
      setOpen(false);
      setForm({ procedure_code: "", tooth: "", units: 1, shade: "", technical_instructions: "", dentist_id: "", status: "planned", notes: "" });
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const markPerformed = async (id) => {
    try { await api.patch(`/clinical/procedures/${id}`, { status: "performed" }); toast.success(t("clinical.markedPerformed")); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadPhoto = async (procId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", "clinical_photo");
    fd.append("file", file);
    try { await api.post(`/clinical/procedures/${procId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(t("clinical.photoUploaded")); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const requestLab = async (procId) => {
    try { const { data } = await api.post(`/clinical/procedures/${procId}/request-lab`); toast.success(`${t("clinical.labRequested")} (${data.order_number})`); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      {canEdit && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-procedure-button" className="mb-4 bg-navy hover:bg-navy-soft text-white gap-2"><Plus className="h-4 w-4" />{t("clinical.recordProcedure")}</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">{t("clinical.recordProcedure")}</DialogTitle>
              <DialogDescription>{t("clinical.recordProcDesc")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="procedure-form">
              <div className="space-y-1.5">
                <Label>{t("clinical.procedure")}</Label>
                <Select value={form.procedure_code} onValueChange={(v) => setForm({ ...form, procedure_code: v })}>
                  <SelectTrigger data-testid="proc-code"><SelectValue placeholder={t("clinical.select")} /></SelectTrigger>
                  <SelectContent>{catalog.map((c) => (<SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("clinical.teeth")}</Label><Input value={form.tooth} onChange={(e) => setForm({ ...form, tooth: e.target.value })} data-testid="proc-tooth" /></div>
                <div className="space-y-1.5"><Label>{t("clinical.unitsCrowns")}</Label><Input type="number" min="1" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} data-testid="proc-units" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("clinical.shade")}</Label><Input value={form.shade} onChange={(e) => setForm({ ...form, shade: e.target.value })} data-testid="proc-shade" placeholder="e.g. A2" /></div>
                <div className="space-y-1.5">
                  <Label>{t("clinical.status")}</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="proc-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="planned">{t("clinical.planned")}</SelectItem><SelectItem value="performed">{t("clinical.performed")}</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>{t("clinical.techInstructions")}</Label><Input value={form.technical_instructions} onChange={(e) => setForm({ ...form, technical_instructions: e.target.value })} data-testid="proc-instructions" /></div>
              <div className="space-y-1.5">
                <Label>{t("appointments.dentist")}</Label>
                <Select value={form.dentist_id} onValueChange={(v) => setForm({ ...form, dentist_id: v })}>
                  <SelectTrigger data-testid="proc-dentist"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                  <SelectContent>{dentists.map((d) => (<SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="proc-submit" disabled={saving} className="bg-navy hover:bg-navy-soft text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("clinical.record")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {procs.length === 0 && <div className="p-10 text-center text-slate-400">{t("clinical.noProcs")}</div>}
        {procs.map((p) => (
          <div key={p.id} data-testid={`procedure-${p.id}`} className="p-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-slate-800">{p.description || p.procedure_code}{p.tooth ? ` · ${t("clinical.tooth")} ${p.tooth}` : ""}{p.units > 1 ? ` · ${p.units} ${t("common.units")}` : ""}{p.shade ? ` · ${t("clinical.shade")} ${p.shade}` : ""}</p>
              <p className="text-sm text-slate-500">{p.dentist_name || t("clinical.unassigned")}{p.performed_by_name ? ` · ${t("clinical.performedBy")} ${p.performed_by_name}` : ""}{p.visit_number ? ` · ${t("common.visit")} ${p.visit_number}` : ""}</p>
              {p.technical_instructions && <p className="text-xs text-slate-400 mt-0.5">{t("clinical.lab")}: {p.technical_instructions}</p>}
              {p.photos?.length > 0 && <p className="text-xs text-blue-600 mt-0.5">{p.photos.length} {t("clinical.photosAttached")}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={p.status === "performed" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 font-normal" : "bg-slate-200 text-slate-600 hover:bg-slate-200 font-normal"}>{p.status === "performed" ? t("clinical.performed") : t("clinical.planned")}</Badge>
              {p.status === "planned" && can("medical_records", "edit") && (
                <Button size="sm" variant="outline" onClick={() => markPerformed(p.id)} data-testid={`perform-${p.id}`}>{t("clinical.markPerformed")}</Button>
              )}
              {can("medical_records", "edit") && (
                <label className="text-xs text-slate-500 hover:text-navy cursor-pointer inline-flex items-center gap-1" data-testid={`photo-label-${p.id}`}>
                  <FileText className="h-3.5 w-3.5" /> {t("clinical.photo")}
                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.heic" className="hidden" data-testid={`photo-input-${p.id}`}
                    onChange={(e) => uploadPhoto(p.id, e.target.files?.[0])} />
                </label>
              )}
              {p.status === "performed" && can("laboratory", "create") && (
                <Button size="sm" variant="outline" onClick={() => requestLab(p.id)} data-testid={`request-lab-${p.id}`}>{t("clinical.requestLab")}</Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
